import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { NowSpinning, QueueItem, Track } from '../types';

// RoomChannel — server-authoritative room state, broadcast to every
// connected client via Supabase Realtime.
//
// One instance per room. Subscribe before reading so post-subscribe
// mutations are caught even if the initial fetch races a write. Reads
// and writes are idempotent: every payload carries a monotonic `seq`,
// and the channel drops any payload older than what it already has.
//
// All mutations go through SECURITY DEFINER Postgres functions (see
// supabase/migrations/20260517000000_queue_first_room_state.sql) so
// the booth invariants (3-track-per-user cap, queue order, "only
// remove your own track") are enforced server-side rather than
// trusted to the browser.

export interface RoomStatePayload {
  roomId: string;
  queue: QueueItem[];
  nowSpinning: NowSpinning | null;
  seq: number;
}

export type RoomStateListener = (state: RoomStatePayload) => void;

interface RoomStateRow {
  room_id: string;
  queue: QueueItem[] | null;
  now_spinning: NowSpinning | null;
  seq: number;
  updated_at: string;
}

function rowToPayload(row: RoomStateRow, roomId: string): RoomStatePayload {
  return {
    roomId: row.room_id ?? roomId,
    queue: Array.isArray(row.queue) ? row.queue : [],
    nowSpinning: row.now_spinning ?? null,
    seq: typeof row.seq === 'number' ? row.seq : 0,
  };
}

export class RoomChannel {
  private roomId: string;
  private channel: RealtimeChannel | null = null;
  private listeners = new Set<RoomStateListener>();
  private latest: RoomStatePayload | null = null;
  private lastSeq = -1;

  constructor(roomId: string) {
    this.roomId = roomId;
  }

  /** Subscribe to row changes, then fetch the initial state. Returns the
   *  initial payload (or null when Supabase isn't configured). Safe to
   *  call once at room boot. */
  async start(): Promise<RoomStatePayload | null> {
    if (!supabase) return null;

    this.channel = supabase
      .channel(`room_state:${this.roomId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'room_states',
          filter: `room_id=eq.${this.roomId}`,
        },
        (payload) => {
          const row = (payload.new ?? payload.old) as RoomStateRow | undefined;
          if (!row) return;
          this.apply(rowToPayload(row, this.roomId));
        },
      )
      .subscribe((status, err) => {
        if (status === 'SUBSCRIBED') {
          console.info(`[roomChannel] subscribed to room_state:${this.roomId}`);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.warn(`[roomChannel] subscription ${status}`, err);
        }
      });

    const { data, error } = await supabase.rpc('room_state_get', {
      p_room_id: this.roomId,
    });
    if (error) {
      console.warn('[roomChannel] initial fetch failed', error);
    } else if (data) {
      this.apply(rowToPayload(data as RoomStateRow, this.roomId));
    }

    return this.latest;
  }

  stop(): void {
    if (this.channel && supabase) supabase.removeChannel(this.channel);
    this.channel = null;
    this.listeners.clear();
  }

  getLatest(): RoomStatePayload | null {
    return this.latest;
  }

  onState(cb: RoomStateListener): () => void {
    this.listeners.add(cb);
    if (this.latest) cb(this.latest);
    return () => { this.listeners.delete(cb); };
  }

  // ----------------------------------------------------------------- RPCs
  //
  // Each RPC returns the updated room state row. We feed it back through
  // `apply()` immediately so the caller's UI updates without waiting for
  // the Realtime round-trip.

  async queueTrack(track: Track, handle: string, avatar3dId: string | null): Promise<void> {
    await this.callMutator('room_queue_track', {
      p_room_id: this.roomId,
      p_track: track,
      p_handle: handle,
      p_avatar_3d_id: avatar3dId,
    });
  }

  async removeQueued(trackId: string): Promise<void> {
    await this.callMutator('room_remove_queued', {
      p_room_id: this.roomId,
      p_track_id: trackId,
    });
  }

  /** Idempotent — only advances if the expected track is still spinning. */
  async advanceIfEnded(expectedTrackId: string): Promise<void> {
    await this.callMutator('room_advance_if_ended', {
      p_room_id: this.roomId,
      p_expected_track_id: expectedTrackId,
    });
  }

  private async callMutator(
    fn: string,
    args: Record<string, unknown>,
  ): Promise<void> {
    if (!supabase) return;
    const { data, error } = await supabase.rpc(fn, args);
    if (error) {
      console.warn(`[roomChannel] ${fn} failed`, error);
      return;
    }
    if (data) this.apply(rowToPayload(data as RoomStateRow, this.roomId));
  }

  private apply(payload: RoomStatePayload): void {
    if (payload.seq < this.lastSeq) return;
    this.lastSeq = payload.seq;
    this.latest = payload;
    for (const l of this.listeners) l(payload);
  }
}
